@if (isEmptyAudience) {
  <!-- No contacts yet: the three ways in, plus the template. -->
  <section class="bk-panel cl-zero" aria-labelledby="cl-zero-title">
    <span class="bk-sym cl-zero__icon" aria-hidden="true">group</span>
    <h2 id="cl-zero-title" class="bk-heading cl-zero__title">No contacts yet</h2>
    <p class="cl-zero__sub">{{ zeroStateLine }}</p>

    <!-- Each way in is offered only with its own permission. -->
    @if (zeroPathCount) {
      <div class="cl-zero__paths" [style.--cl-paths]="zeroPathCount">
        @if (can.create) {
          <button type="button" class="cl-path" (click)="openCreateContact()">
            <span class="bk-sym" aria-hidden="true">person_add</span>
            <span class="cl-path__title">Add one contact</span>
            <span class="cl-path__hint">A name and a phone number</span>
          </button>
          <button type="button" class="cl-path" (click)="goToCreateMultipleContactsPage()">
            <span class="bk-sym" aria-hidden="true">group_add</span>
            <span class="cl-path__title">Add several</span>
            <span class="cl-path__hint">Type a few people in rows</span>
          </button>
        }
        @if (can.import) {
          <button type="button" class="cl-path" (click)="goToCsvUpload()">
            <span class="bk-sym" aria-hidden="true">upload_file</span>
            <span class="cl-path__title">Import a spreadsheet</span>
            <span class="cl-path__hint">CSV or Excel, any size</span>
          </button>
        }
      </div>
    }

    @if (can.import) {
      <a class="bk-more cl-zero__template" href="/files/blackkonnect-template.csv" download>
        Download the import template
        <span class="bk-sym" aria-hidden="true">download</span>
      </a>
    }
  </section>
} @else {
  <!-- Search, filters, sort and export -->
  <div class="cl-toolbar">
    <app-search-input
      class="cl-search"
      placeholder="Search name, phone or email"
      (valueChange)="updateSearchTerm($event)"
    />

    <div class="cl-filter" (clickOutside)="closeFilters()">
      <button
        #filterTrigger
        type="button"
        class="cl-filter__trigger"
        [class.is-active]="filtersActive"
        aria-haspopup="dialog"
        [attr.aria-expanded]="showFilters"
        [attr.aria-controls]="showFilters ? 'cl-filter-panel' : null"
        (click)="toggleFilters()"
      >
        <span class="bk-sym" aria-hidden="true">tune</span>
        Filters
        @if (activeFilterCount) {
          <span class="cl-filter__count">{{ activeFilterCount }}<span class="bk-sr-only"> active</span></span>
        }
      </button>

      @if (showFilters) {
        <div #filterPanel id="cl-filter-panel" class="cl-filter__panel" role="dialog" aria-label="Filter contacts">
          <div class="bk-field">
            <label class="bk-field__label" for="cl-f-added">Added</label>
            <select
              id="cl-f-added"
              class="bk-field__input"
              [value]="addedFilter"
              (change)="setAddedFilter($any($event.target).value)"
            >
              @for (option of addedOptions; track option.value) {
                <option [value]="option.value" [selected]="option.value === addedFilter">{{ option.label }}</option>
              }
            </select>
          </div>

          <div class="bk-field">
            <label class="bk-field__label" for="cl-f-tag">Tag</label>
            <select
              id="cl-f-tag"
              class="bk-field__input"
              [disabled]="filterOptionsLoading && !tagOptions.length"
              (change)="setTagFilter($any($event.target).value)"
            >
              <option value="" [selected]="!tagFilter">Any tag</option>
              @for (tag of tagOptions; track tag.id) {
                <option [value]="tag.id" [selected]="tagFilter === '' + tag.id">{{ tag.name }}</option>
              }
            </select>
            @if (tagOptionsTruncated) {
              <p class="bk-field__hint">Showing the first {{ tagOptions.length }} tags by name.</p>
            }
          </div>

          <div class="bk-field">
            <label class="bk-field__label" for="cl-f-org">Organization</label>
            <select
              id="cl-f-org"
              class="bk-field__input"
              [disabled]="filterOptionsLoading && !organizationOptions.length"
              (change)="setOrganizationFilter($any($event.target).value)"
            >
              <option value="" [selected]="!organizationFilter">Any organization</option>
              @for (org of organizationOptions; track org.id) {
                <option [value]="org.id" [selected]="organizationFilter === '' + org.id">{{ org.name }}</option>
              }
            </select>
            @if (organizationOptionsTruncated) {
              <p class="bk-field__hint">Showing the first {{ organizationOptions.length }} organizations by name.</p>
            }
          </div>

          @if (filterOptionsError) {
            <p class="bk-field__error">
              <span class="bk-sym" aria-hidden="true">error</span>
              <span>{{ filterOptionsError }}</span>
            </p>
            <button type="button" class="bk-more" (click)="loadFilterOptions(true)">Load them again</button>
          }

          <div class="cl-filter__foot">
            <button type="button" class="bk-more" [disabled]="!filtersActive" (click)="clearAllFilters()">
              Clear filters
            </button>
            <button type="button" class="bk-btn bk-btn--ghost cl-filter__done" (click)="closeFilters()">Done</button>
          </div>
        </div>
      }
    </div>

    <app-sort-dropdown
      [groups]="sortGroups"
      [selectedValue]="sortCriteria"
      (selectionChange)="updateSortCriteria($any($event))"
    />

    <span class="cl-toolbar__spacer"></span>

    <p class="cl-count" aria-live="polite">{{ hasLoaded ? countLine : '' }}</p>

    <button
      type="button"
      class="bk-btn bk-btn--ghost"
      [disabled]="exporting || !totalElements"
      (click)="exportContacts()"
    >
      <span class="bk-sym" aria-hidden="true">download</span>
      {{ exportLabel }}
    </button>
  </div>

  <!-- Active filters -->
  @if (filtersActive) {
    <div class="cl-chips" role="group" aria-label="Active filters">
      @if (addedFilter !== 'any') {
        <span class="bk-chip cl-chip">
          Added {{ addedLabel.toLowerCase() }}
          <button type="button" class="cl-chip__x" aria-label="Remove the added filter" (click)="clearFilter('added')">
            <span class="bk-sym" aria-hidden="true">close</span>
          </button>
        </span>
      }
      @if (tagFilter) {
        <span class="bk-chip cl-chip">
          Tag: {{ tagLabel }}
          <button type="button" class="cl-chip__x" aria-label="Remove the tag filter" (click)="clearFilter('tag')">
            <span class="bk-sym" aria-hidden="true">close</span>
          </button>
        </span>
      }
      @if (organizationFilter) {
        <span class="bk-chip cl-chip">
          Organization: {{ organizationLabel }}
          <button
            type="button"
            class="cl-chip__x"
            aria-label="Remove the organization filter"
            (click)="clearFilter('organization')"
          >
            <span class="bk-sym" aria-hidden="true">close</span>
          </button>
        </span>
      }
      <button type="button" class="bk-more" (click)="clearAllFilters()">Clear all</button>
    </div>
  }

  <!-- Export progress and notices. The status line is always present so each change is read out. -->
  <p class="bk-sr-only" role="status">{{ exportStatus }}</p>
  @if (exporting) {
    <div class="cl-notice">
      <span class="cl-notice__text">
        Exporting {{ formatCount(exportDone) }} of {{ formatCount(exportTarget) }} contacts
      </span>
      <span class="cl-progress" aria-hidden="true">
        <span class="cl-progress__fill" [style.transform]="'scaleX(' + exportProgress + ')'"></span>
      </span>
      <button type="button" class="bk-more" (click)="cancelExport()">Cancel</button>
    </div>
  } @else if (exportNotice || exportError) {
    <div class="cl-notice" [class.is-error]="!!exportError">
      <span class="cl-notice__text">{{ exportError || exportNotice }}</span>
      <button type="button" class="bk-icon-btn" aria-label="Dismiss" (click)="dismissExportNotice()">
        <span class="bk-sym" aria-hidden="true">close</span>
      </button>
    </div>
  }

  <!-- Selection bar: what to do with the ticked contacts -->
  <p class="bk-sr-only" role="status">{{ selectedContacts.length ? selectionLabel : '' }}</p>
  @if (selectedContacts.length) {
    <div class="cl-select" role="region" aria-label="Selected contacts">
      <span class="cl-select__count">{{ selectionLabel }}</span>
      <span class="cl-select__actions">
        <button type="button" class="cl-select__btn" (click)="handleAction('send-sms')">
          <span class="bk-sym" aria-hidden="true">send</span>
          Send SMS
        </button>
        @if (can.manageGroups) {
          <button type="button" class="cl-select__btn" (click)="handleAction('move-to-organization')">
            <span class="bk-sym" aria-hidden="true">domain_add</span>
            Add to organization
          </button>
          <button type="button" class="cl-select__btn" (click)="handleAction('add-tags')">
            <span class="bk-sym" aria-hidden="true">sell</span>
            Add tags
          </button>
        }
        @if (can.delete) {
          <button type="button" class="cl-select__btn is-danger" (click)="handleAction('delete')">
            <span class="bk-sym" aria-hidden="true">delete</span>
            Delete
          </button>
        }
      </span>
      <button type="button" class="cl-select__clear" (click)="resetSelectedContacts()">Clear selection</button>
    </div>
  }
}

<!-- The table stays mounted so its page, size and selection survive the zero state. -->
<div class="cl-table" [hidden]="isEmptyAudience">
  <contacts-table
    (updateSelectedContacts)="handleContactsSelection($event)"
    [params]="reactiveParams"
    [searchTerm]="searchTerm"
    [filtered]="filtersActive"
    (clearFilters)="clearSearchAndFilters()"
  />
</div>

<!-- MODALS -->
<assign-multiple-contacts-to-organizations
  [open]="showAssignMultipleContactsToOrg"
  (close)="closeAssignMultipleContactsToOrg()"
  [selectedContacts]="selectedContacts"
  (resetSelectedContacts)="resetSelectedContacts()"
/>
<assign-multiple-contacts-to-tags
  [selectedContacts]="selectedContacts"
  [open]="showAssignMultipleContactsToTags"
  (close)="closeAssignMultipleContactsToTags()"
  (resetSelectedContacts)="resetSelectedContacts()"
/>

<app-prompt-card
  [buttonText]="selectedContacts.length === 1 ? 'Delete contact' : 'Delete ' + selectedContacts.length + ' contacts'"
  [title]="selectedContacts.length === 1 ? 'Delete this contact?' : 'Delete ' + selectedContacts.length + ' contacts?'"
  tone="danger"
  impact="This cannot be undone."
  impactIcon="warning"
  [openModal]="showDeleteSelectedPrompt"
  (onClose)="closeDeleteSelectedPrompt()"
  (onButtonClick)="deleteSelectedContacts()"
  [loading]="contactsService.isLoading"
>
  <p class="cl-prompt">
    The selected contacts will be removed from your contacts, organizations and tags.
  </p>
</app-prompt-card>

@if (showCreateContact) {
  <create-contact-form (closeModalEvent)="closeCreateContact()" />
}
